import React, { ComponentProps, useRef, useState } from 'react';
import { asFetchableBlock, useInvalidateRefreshableBlock, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { BlockBar, BlockBarAnchor, useBlockBarCreateHref } from '../../_lib/components/BlockBar';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { Icons } from '../../components/atoms/SVG';
import { DefaultView, OptionalEditView, Views } from '../../types/BlockWithViews';
import {
  FavoritedPagesBlockDefaultView,
} from './partials/FavoritedPagesBlockDefaultView';
import { FavoritedPagesBlockEditView } from './partials/FavoritedPagesBlockEditView';
import { useHtmlValidId } from '../../hooks/useHtmlValidId';
import { EditButtonRefContext } from './partials/EditButtonRefContext';

export type FavoritedPagesProps = Views<DefaultView<FavoritedPagesBlockDefaultView> &
  OptionalEditView<FavoritedPagesBlockEditView>>;

function FavoritedPages({ views }: FavoritedPagesProps) {
  const { t } = useSonTranslation('FavoritedPages');
  const { refresh } = useRefreshBlock();
  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();
  const id = useHtmlValidId();
  const editButtonRef = useRef<HTMLAnchorElement>(null);

  const [activeView, setActiveView] = useState<'default' | 'edit'>('default');
  const currentActiveViewHref = useBlockBarCreateHref(
    activeView,
    { default: views.edit?.href, edit: views.default?.href },
    id,
  );

  return (
    <>
      <BlockBar heading={t('editFavourites')} hierarchy={4} modifier="border-bottom">
        <BlockBarAnchor
          ref={editButtonRef}
          href={currentActiveViewHref}
          text={t('editFavourites')}
          svgSpriteId={activeView === 'default' ? Icons.EDIT_PENCIL : Icons.DECLINE_CROSS}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            if (activeView === 'default') {
              setActiveView('edit');
            } else {
              setActiveView('default');
              refresh();
              invalidateRefreshableBlock(BlockType.pagemetainfo);
            }
          }}
          aria-controls={id}
          aria-expanded={activeView === 'edit'}
        />
      </BlockBar>
      <div id={id}>
        {activeView === 'default'
          ? <FavoritedPagesBlockDefaultView {...views.default} />
          : null}
        {activeView === 'edit'
          ? <EditButtonRefContext.Provider value={editButtonRef}>
              <FavoritedPagesBlockEditView {...views.edit as FavoritedPagesBlockEditView} />
            </EditButtonRefContext.Provider>
          : null}
      </div>
    </>
  );
}
export const FavoritedPagesBlock = asFetchableBlock(FavoritedPages, BlockType.favoritedpages, BlockType.favoritedpages);
export type FavoritedPagesBlock = ComponentProps<typeof FavoritedPagesBlock>;
